import { Injectable } from '@angular/core';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { v4 as uuid } from 'uuid';
import cloneDeep from 'lodash.clonedeep';
import {
  DprDetails,
  DprDetailsSync,
  DprApprover,
  DprProgressDetail,
  DprSummaryAttachment,
  DprConsumableDetail,
  DprPersonnelDetail,
  DprHseDetail,
  DprWeatherDetail,
  DprHidden,
  DprAttachment,
  DprStatusTrackerDetails,
  DprTableOfColumnDetail,
  DprProgressWithoutTargetDetail,
  DprFreeTextField,
  DprFreeTextFieldDetail,
} from '@shared/models/dpr';
import { SyncService } from '@shared/services/grid';
import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import { Observable, take, mergeMap, of, withLatestFrom } from 'rxjs';
import { DprDetailsSyncModelBuilderService } from '.';
import {
  DprAttachmentService,
  DprDetailsWarningService,
  NamingConventionService,
} from '@shared/services/dpr';
import {
  WriteDprActivity,
  WriteDprActivityCustomData,
  WriteDprForecastActivity,
  WriteDprForecastActivityCustomData,
  WriteDprForecastSubActivity,
  WriteDprParallelActivity,
  WriteDprParallelActivityCustomData,
  WriteDprParallelSubActivity,
  WriteDprSubActivity,
} from '@dpr-details/models/sync';
import {
  mapSyncForecastActivitiesToSyncForecastGridData,
  withoutDerivedStatusTrackerCells,
  withoutDerivedTableOfColumnItems,
} from '@dpr-details/utils';

@UntilDestroy()
@Injectable()
export class DprDetailsOfflineRevisionService {
  constructor(
    private offlineFacade: OfflineFacade,
    private syncService: SyncService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private namingConventionService: NamingConventionService,
    private dprDetailsWarningService: DprDetailsWarningService,
    private dprAttachmentService: DprAttachmentService,
    private userDetailsFacade: UserDetailsFacade
  ) {}

  createNewRevision(dprDetailId: number | string): Observable<DprDetails> {
    return this.offlineFacade.getDprDetails(dprDetailId).pipe(
      take(1),
      mergeMap((existingDprDetails: DprDetails) => {
        // create a deep copy to make the object editable
        const dprDetails: DprDetails = cloneDeep(existingDprDetails);

        if (!dprDetails) {
          return of(null);
        }

        dprDetails.id = uuid();
        dprDetails.revision += 1;
        dprDetails.lockedDate = null;
        dprDetails.isSignDprDetailsUploaded = false;

        if (dprDetails.dprActivities) {
          dprDetails.dprActivities.forEach((item) => {
            item.id = uuid();
            item.dprDetailId = dprDetails.id;
            if (item.dprSubActivities) {
              item.dprSubActivities.forEach((sub) => {
                sub.id = uuid();
                sub.dprActivityId = item.id;
              });
            }
            if (item.dprActivityAttachments) {
              item.dprActivityAttachments.forEach(this.setDprAttachmentIds);
            }
            if (item.dprActivityCustomDataList) {
              item.dprActivityCustomDataList.forEach((cd) => {
                cd.id = uuid();
                cd.dprActivityId = item.id;
              });
            }
          });
        }

        if (dprDetails.dprParallelActivities) {
          dprDetails.dprParallelActivities.forEach((item) => {
            item.id = uuid();
            item.dprDetailId = dprDetails.id;
            if (item.dprParallelSubActivityList) {
              item.dprParallelSubActivityList.forEach((sub) => {
                sub.id = uuid();
                sub.dprParallelActivityId = item.id;
              });
            }
            if (item.dprParallelActivityAttachments) {
              item.dprParallelActivityAttachments.forEach(
                this.setDprAttachmentIds
              );
            }
            if (item.dprParallelActivityCustomDataList) {
              item.dprParallelActivityCustomDataList.forEach((cd) => {
                cd.id = uuid();
                cd.dprParallelActivityId = item.id;
              });
            }
          });
        }

        if (dprDetails.dprApprovers) {
          dprDetails.dprApprovers.forEach((approver) => {
            approver.dprDetailId = dprDetails.id;
            approver.id = uuid();
          });
        }

        if (dprDetails.dprStatusTracker) {
          dprDetails.dprStatusTracker.id = uuid();
          dprDetails.dprStatusTracker.dprDetailId = dprDetails.id;
          if (dprDetails.dprStatusTracker.dprStatusTrackerDetails) {
            dprDetails.dprStatusTracker.dprStatusTrackerDetails.forEach(
              (item) => {
                item.dprStatusTrackerId = dprDetails.dprStatusTracker.id;
                item.id = uuid();
              }
            );
          }
        }

        if (dprDetails.dprWeather) {
          dprDetails.dprWeather.id = uuid();
          dprDetails.dprWeather.dprDetailId = dprDetails.id;
          if (dprDetails.dprWeather.dprWeatherDetails) {
            dprDetails.dprWeather.dprWeatherDetails.forEach((item) => {
              item.dprWeatherId = dprDetails.dprWeather.id;
              item.id = uuid();
              item.dprWeatherDetailsItems.forEach((d) => {
                d.id = uuid();
                d.dprWeatherDetailId = item.id;
              });
            });
          }
          if (dprDetails.dprWeather.dprWeatherAttachments) {
            dprDetails.dprWeather.dprWeatherAttachments.forEach(
              this.setDprAttachmentIds
            );
          }
        }
        if (dprDetails.dprHSE) {
          dprDetails.dprHSE.id = uuid();
          dprDetails.dprHSE.dprDetailId = dprDetails.id;
          if (dprDetails.dprHSE.dprhseDetails) {
            dprDetails.dprHSE.dprhseDetails.forEach((item) => {
              item.id = uuid();
              item.dprhseId = dprDetails.dprHSE.id;
            });
          }
          if (dprDetails.dprHSE.dprhseAttachments) {
            dprDetails.dprHSE.dprhseAttachments.forEach(
              this.setDprAttachmentIds
            );
          }
        }

        if (dprDetails.dprPersonnel) {
          dprDetails.dprPersonnel.id = uuid();
          dprDetails.dprPersonnel.dprDetailId = dprDetails.id;
          if (dprDetails.dprPersonnel.dprPersonnelDetails) {
            dprDetails.dprPersonnel.dprPersonnelDetails.forEach((item) => {
              item.id = uuid();
              item.dprPersonnelId = dprDetails.dprPersonnel.id;
            });
          }
          if (dprDetails.dprPersonnel.dprPersonnelAttachments) {
            dprDetails.dprPersonnel.dprPersonnelAttachments.forEach(
              this.setDprAttachmentIds
            );
          }
        }

        if (dprDetails.dprConsumable) {
          dprDetails.dprConsumable.id = uuid();
          dprDetails.dprConsumable.dprDetailId = dprDetails.id;
          if (dprDetails.dprConsumable.dprConsumableDetails) {
            dprDetails.dprConsumable.dprConsumableDetails.forEach((item) => {
              item.id = uuid();
              item.dprConsumableId = dprDetails.dprConsumable.id;
            });
          }
          if (dprDetails.dprConsumable.dprConsumableAttachments) {
            dprDetails.dprConsumable.dprConsumableAttachments.forEach(
              this.setDprAttachmentIds
            );
          }
        }

        if (dprDetails.dprSummary) {
          dprDetails.dprSummary.id = uuid();
          dprDetails.dprSummary.dprDetailId = dprDetails.id;
          if (dprDetails.dprSummary.dprSummaryAttachments) {
            dprDetails.dprSummary.dprSummaryAttachments.forEach(
              this.setDprAttachmentIds
            );
          }
        }

        if (dprDetails.dprAppendixAttachments) {
          dprDetails.dprAppendixAttachments.forEach(this.setDprAttachmentIds);
        }

        if (dprDetails.dprForecastActivities) {
          dprDetails.dprForecastActivities.forEach((item) => {
            item.id = uuid();
            item.dprDetailId = dprDetails.id;
            if (item.dprForecastSubActivities) {
              item.dprForecastSubActivities.forEach((sub) => {
                sub.id = uuid();
                sub.dprForecastActivityId = item.id;
              });
            }

            if (item.dprForecastActivityCustomDataList) {
              item.dprForecastActivityCustomDataList.forEach((cd) => {
                cd.id = uuid();
                cd.dprForecastActivityId = item.id;
              });
            }
          });
        }

        if (dprDetails.dprFreeTextFields) {
          dprDetails.dprFreeTextFields.forEach((item) => {
            item.id = uuid();
            item.dprDetailId = dprDetails.id;
            item.dprFreeTextFieldDetails.forEach((x) => {
              x.id = uuid();
              x.dprFreeTextFieldId = item.id;
            });
          });
        }

        if (dprDetails.dprHidden) {
          dprDetails.dprHidden.forEach((item) => {
            item.id = uuid();
            item.dprDetailId = dprDetails.id;
          });
        }

        if (dprDetails.dprProgress) {
          dprDetails.dprProgress.id = uuid();
          dprDetails.dprProgress.dprDetailId = dprDetails.id;
          if (dprDetails.dprProgress.dprProgressDetails) {
            dprDetails.dprProgress.dprProgressDetails.forEach((item) => {
              item.id = uuid();
              item.dprProgressId = dprDetails.dprProgress.id;
            });
          }
          if (dprDetails.dprProgress.dprProgressAttachments) {
            dprDetails.dprProgress.dprProgressAttachments.forEach(
              this.setDprAttachmentIds
            );
          }
        }

        if (dprDetails.dprProgressWithoutTarget) {
          dprDetails.dprProgressWithoutTarget.id = uuid();
          dprDetails.dprProgressWithoutTarget.dprDetailId = dprDetails.id;
          if (
            dprDetails.dprProgressWithoutTarget.dprProgressWithoutTargetDetails
          ) {
            dprDetails.dprProgressWithoutTarget.dprProgressWithoutTargetDetails.forEach(
              (item) => {
                item.id = uuid();
                item.dprProgressWithoutTargetId =
                  dprDetails.dprProgressWithoutTarget.id;
              }
            );
          }

          if (
            dprDetails.dprProgressWithoutTarget
              .dprProgressWithoutTargetAttachments
          ) {
            dprDetails.dprProgressWithoutTarget.dprProgressWithoutTargetAttachments.forEach(
              this.setDprAttachmentIds
            );
          }
        }

        if (dprDetails.dprTableOfColumns) {
          dprDetails.dprTableOfColumns.forEach((dprTableOfColumn) => {
            dprTableOfColumn.id = uuid();
            dprTableOfColumn.dprDetailId = dprDetails.id;
            if (dprTableOfColumn.dprTableOfColumnsDetails) {
              dprTableOfColumn.dprTableOfColumnsDetails.forEach((detail) => {
                detail.id = uuid();
                detail.dprTableOfColumnId = dprTableOfColumn.id;
                if (detail.dprTableOfColumnDetailItems) {
                  detail.dprTableOfColumnDetailItems.forEach((item) => {
                    item.id = uuid();
                    item.dprTableOfColumnDetailId = detail.id;
                  });
                }
              });
            }
            if (dprTableOfColumn.dprTableOfColumnsAttachment) {
              dprTableOfColumn.dprTableOfColumnsAttachment.forEach(
                this.setDprAttachmentIds
              );
            }
          });
        }

        // reset approvals for new revision
        dprDetails.dprOnlineApprovals = [];
        dprDetails.approveStatus = null;

        this.namingConventionService.applyNamingConvention(dprDetails);
        this.dprDetailsWarningService.updateDprWarningStatus(dprDetails);

        return of(dprDetails);
      })
    );
  }

  createChanges(
    dprDetails: DprDetails,
    dprDetailsChanges$: Observable<DprDetailsSync>
  ): Observable<DprDetailsSync> {
    return dprDetailsChanges$.pipe(
      untilDestroyed(this),
      withLatestFrom(this.userDetailsFacade.query.projectSetup$),
      mergeMap(([dprDetailsChanges, projectSetup]) => {
        if (!dprDetails) {
          return null;
        }

        const changes: DprDetailsSync = cloneDeep(dprDetailsChanges);

        const syncDprActivities =
          this.syncService.getDefaultSyncModel<WriteDprActivity>();
        const syncDprParallelActivities =
          this.syncService.getDefaultSyncModel<WriteDprParallelActivity>();

        if (dprDetails.dprActivities) {
          dprDetails.dprActivities.forEach((activity) => {
            const item: WriteDprActivity = {
              id: activity.id,
              dprDetailId: dprDetails.id,
              activityGroupId: activity.activityGroupId,
              activityId: activity.activityId,
              locationId: activity.locationId,
              start: activity.start,
              finish: activity.finish,
              comment: activity.comment,
              syncDprSubActivities: null,
              syncDprActivityCustomDataList: null,
              syncDprActivityAttachments: null,
            };

            item.syncDprSubActivities =
              this.syncService.getDefaultSyncModel<WriteDprSubActivity>();
            item.syncDprSubActivities.addedList =
              activity.dprSubActivities || [];

            item.syncDprActivityCustomDataList =
              this.syncService.getDefaultSyncModel<WriteDprActivityCustomData>();
            item.syncDprActivityCustomDataList.addedList =
              activity.dprActivityCustomDataList || [];

            item.syncDprActivityAttachments =
              this.syncService.getDefaultSyncModel<DprAttachment>();
            item.syncDprActivityAttachments.addedList =
              activity.dprActivityAttachments || [];

            syncDprActivities.addedList = [
              ...syncDprActivities.addedList,
              item,
            ];
          });
        }

        if (dprDetails.dprParallelActivities) {
          dprDetails.dprParallelActivities.forEach((activity) => {
            const item: WriteDprParallelActivity = {
              id: activity.id,
              dprDetailId: dprDetails.id,
              activityGroupId: activity.activityGroupId,
              activityId: activity.activityId,
              locationId: activity.locationId,
              start: activity.start,
              finish: activity.finish,
              comment: activity.comment,
              parallelActivityType: activity.parallelActivityType,
              syncDprParallelSubActivities: null,
              syncDprParallelActivityCustomDataList: null,
              syncDprParallelActivityAttachments: null,
            };

            item.syncDprParallelSubActivities =
              this.syncService.getDefaultSyncModel<WriteDprParallelSubActivity>();
            item.syncDprParallelSubActivities.addedList =
              activity.dprParallelSubActivityList || [];

            item.syncDprParallelActivityCustomDataList =
              this.syncService.getDefaultSyncModel<WriteDprParallelActivityCustomData>();
            item.syncDprParallelActivityCustomDataList.addedList =
              activity.dprParallelActivityCustomDataList || [];

            item.syncDprParallelActivityAttachments =
              this.syncService.getDefaultSyncModel<DprAttachment>();
            item.syncDprParallelActivityAttachments.addedList =
              activity.dprParallelActivityAttachments || [];

            syncDprParallelActivities.addedList = [
              ...syncDprParallelActivities.addedList,
              item,
            ];
          });
        }

        changes.dprActivities = syncDprActivities;
        changes.dprParallelActivities = syncDprParallelActivities;

        if (dprDetails.dprApprovers) {
          const syncDprApprovers =
            this.syncService.getDefaultSyncModel<DprApprover>();
          syncDprApprovers.addedList = dprDetails.dprApprovers || [];
          changes.syncDprApprovers = syncDprApprovers;
        }

        if (dprDetails.dprStatusTracker) {
          const syncDprStatusTrackerDetails =
            this.syncService.getDefaultSyncModel<DprStatusTrackerDetails>();
          syncDprStatusTrackerDetails.addedList =
            withoutDerivedStatusTrackerCells(
              dprDetails.dprStatusTracker.dprStatusTrackerDetails,
              projectSetup?.statusTrackers
            );
          changes.dprStatusTracker =
            this.dprDetailsSyncModelBuilderService.forDprStatusTracker(
              dprDetails.dprSubprojectId,
              dprDetails.dprStatusTracker.id,
              dprDetails.id,
              syncDprStatusTrackerDetails
            ).dprStatusTracker;
        }

        if (dprDetails.dprWeather) {
          const syncDprWeatherDetails =
            this.syncService.getDefaultSyncModel<DprWeatherDetail>();
          const syncDprWeatherAttachments =
            this.syncService.getDefaultSyncModel<DprAttachment>();

          syncDprWeatherDetails.addedList =
            dprDetails.dprWeather.dprWeatherDetails || [];
          syncDprWeatherAttachments.addedList =
            dprDetails.dprWeather.dprWeatherAttachments || [];

          changes.dprWeather =
            this.dprDetailsSyncModelBuilderService.forDprWeather(
              dprDetails.dprSubprojectId,
              dprDetails.id,
              dprDetails.dprWeather.id,
              syncDprWeatherDetails,
              this.dprAttachmentService.mapSyncDprAttachmentToGridData(
                syncDprWeatherAttachments
              ),
              dprDetails.dprWeather.comment
            ).dprWeather;
        }

        if (dprDetails.dprHSE) {
          const syncDprHseDetails =
            this.syncService.getDefaultSyncModel<DprHseDetail>();
          const syncDprHseAttachments =
            this.syncService.getDefaultSyncModel<DprAttachment>();

          syncDprHseDetails.addedList = (
            dprDetails.dprHSE.dprhseDetails || []
          ).filter((x) => x.today != null);
          syncDprHseAttachments.addedList =
            dprDetails.dprHSE.dprhseAttachments || [];

          changes.dprHSE = this.dprDetailsSyncModelBuilderService.forDprHse(
            dprDetails.dprSubprojectId,
            dprDetails.id,
            dprDetails.dprHSE.id,
            syncDprHseDetails,
            this.dprAttachmentService.mapSyncDprAttachmentToGridData(
              syncDprHseAttachments
            ),
            dprDetails.dprHSE.comment
          ).dprHse;
        }

        if (dprDetails.dprPersonnel) {
          const syncDprPersonnelDetails =
            this.syncService.getDefaultSyncModel<DprPersonnelDetail>();
          const syncDprPersonnelAttachments =
            this.syncService.getDefaultSyncModel<DprAttachment>();

          syncDprPersonnelDetails.addedList = (
            dprDetails.dprPersonnel.dprPersonnelDetails || []
          ).filter(
            (x) =>
              x.today != null ||
              x.added != null ||
              x.used != null ||
              x.workedHours != null
          );

          syncDprPersonnelAttachments.addedList =
            dprDetails.dprPersonnel.dprPersonnelAttachments || [];

          changes.dprPersonnel =
            this.dprDetailsSyncModelBuilderService.forDprPersonnel(
              dprDetails.dprSubprojectId,
              dprDetails.id,
              dprDetails.dprPersonnel.id,
              syncDprPersonnelDetails,
              this.dprAttachmentService.mapSyncDprAttachmentToGridData(
                syncDprPersonnelAttachments
              ),
              dprDetails.dprPersonnel.comment
            ).dprPersonnel;
        }

        if (dprDetails.dprConsumable) {
          const syncDprConsumableDetails =
            this.syncService.getDefaultSyncModel<DprConsumableDetail>();
          const syncDprConsumableAttachments =
            this.syncService.getDefaultSyncModel<DprAttachment>();

          syncDprConsumableDetails.addedList = (
            dprDetails.dprConsumable.dprConsumableDetails || []
          ).filter((x) => x.today != null || x.added != null || x.used != null);

          syncDprConsumableAttachments.addedList =
            dprDetails.dprConsumable.dprConsumableAttachments || [];

          changes.dprConsumable =
            this.dprDetailsSyncModelBuilderService.forDprConsumable(
              dprDetails.dprSubprojectId,
              dprDetails.id,
              dprDetails.dprConsumable.id,
              syncDprConsumableDetails,
              this.dprAttachmentService.mapSyncDprAttachmentToGridData(
                syncDprConsumableAttachments
              ),
              dprDetails.dprConsumable.comment
            ).dprConsumable;
        }

        if (dprDetails.dprSummary) {
          const syncDprSummaryAttachments =
            this.syncService.getDefaultSyncModel<DprSummaryAttachment>();

          syncDprSummaryAttachments.addedList =
            dprDetails.dprSummary.dprSummaryAttachments || [];

          changes.dprSummary =
            this.dprDetailsSyncModelBuilderService.forDprSummary(
              dprDetails.dprSubprojectId,
              dprDetails.id,
              dprDetails.dprSummary.id,
              syncDprSummaryAttachments,
              dprDetails.dprSummary.currentComment,
              dprDetails.dprSummary.nextComment,
              dprDetails.dprSummary.externalComment
            ).dprSummary;
        }

        if (dprDetails.dprAppendixAttachments) {
          const syncDprAppendixAttachments =
            this.syncService.getDefaultSyncModel<DprAttachment>();

          syncDprAppendixAttachments.addedList =
            dprDetails.dprAppendixAttachments || [];

          changes.syncDprAppendixAttachments =
            this.dprDetailsSyncModelBuilderService.forDprAppendix(
              dprDetails.dprSubprojectId,
              dprDetails.id,
              this.dprAttachmentService.mapSyncDprAttachmentToGridData(
                syncDprAppendixAttachments
              )
            ).syncDprAppendixAttachments;
        }

        if (dprDetails.dprForecastActivities) {
          const syncDprForecastActivities =
            this.syncService.getDefaultSyncModel<WriteDprForecastActivity>();
          dprDetails.dprForecastActivities.forEach((activity) => {
            const item: WriteDprForecastActivity = {
              id: activity.id,
              dprDetailId: dprDetails.id,
              activityGroupId: activity.activityGroupId,
              activityId: activity.activityId,
              locationId: activity.locationId,
              forecastCategoryId: activity.forecastCategoryId,
              date: new Date(activity.date),
              start: activity.start,
              finish: activity.finish,
              syncDprForecastSubActivities: null,
              syncDprForecastActivityCustomDataList: null,
              comment: null,
            };

            item.syncDprForecastSubActivities =
              this.syncService.getDefaultSyncModel<WriteDprForecastSubActivity>();
            item.syncDprForecastSubActivities.addedList =
              activity.dprForecastSubActivities || [];

            item.syncDprForecastActivityCustomDataList =
              this.syncService.getDefaultSyncModel<WriteDprForecastActivityCustomData>();
            item.syncDprForecastActivityCustomDataList.addedList =
              activity.dprForecastActivityCustomDataList || [];

            syncDprForecastActivities.addedList = [
              ...syncDprForecastActivities.addedList,
              item,
            ];
          });

          changes.dprForecastActivities =
            this.dprDetailsSyncModelBuilderService.forDprForecastActivities(
              dprDetails.dprSubprojectId,
              dprDetails.id,
              mapSyncForecastActivitiesToSyncForecastGridData(
                syncDprForecastActivities
              )
            ).syncDprForecastActivities;
        }

        if (dprDetails.dprFreeTextFields) {
          let syncDprFreeTextFields = [];

          const syncDprFreeTextField =
            this.syncService.getDefaultSyncModel<DprFreeTextField>();
          dprDetails.dprFreeTextFields.forEach((freeTextField) => {
            const item = {
              id: freeTextField.id,
              dprDetailId: freeTextField.dprDetailId,
              freeTextFieldId: freeTextField.freeTextFieldId,
              dprFreeTextFieldDetails: [],
            };

            const syncDprFreeTextFieldsDetails =
              this.syncService.getDefaultSyncModel<DprFreeTextFieldDetail>();

            freeTextField.dprFreeTextFieldDetails.forEach((x) => {
              const itemDetail = {
                id: x.id,
                dprFreeTextFieldId: x.dprFreeTextFieldId,
                freeTextFieldItemId: x.freeTextFieldItemId,
                comment: x.comment,
              };

              item.dprFreeTextFieldDetails.push(itemDetail);
              syncDprFreeTextFieldsDetails.addedList = [
                ...syncDprFreeTextFieldsDetails.addedList,
                itemDetail,
              ];
            });

            syncDprFreeTextField.addedList = [
              ...syncDprFreeTextField.addedList,
              item,
            ];

            const freeTextFieldItem =
              this.dprDetailsSyncModelBuilderService.forDprFreeTextFields(
                dprDetails.dprSubprojectId,
                dprDetails.id,
                syncDprFreeTextFieldsDetails,
                freeTextField.freeTextFieldId,
                freeTextField.id
              ).dprFreeTextField;

            syncDprFreeTextFields = [
              ...syncDprFreeTextFields,
              freeTextFieldItem,
            ];
          });

          changes.dprFreeTextFields = syncDprFreeTextFields;
        }

        if (dprDetails.dprProgress) {
          const syncDprProgressDetails =
            this.syncService.getDefaultSyncModel<DprProgressDetail>();
          const syncDprProgressAttachments =
            this.syncService.getDefaultSyncModel<DprAttachment>();

          syncDprProgressDetails.addedList = (
            dprDetails.dprProgress.dprProgressDetails || []
          ).filter((x) => x.clearedToday != null);
          syncDprProgressAttachments.addedList =
            dprDetails.dprProgress.dprProgressAttachments || [];

          changes.dprProgress =
            this.dprDetailsSyncModelBuilderService.forDprProgress(
              dprDetails.dprSubprojectId,
              dprDetails.id,
              dprDetails.dprProgress.id,
              syncDprProgressDetails,
              this.dprAttachmentService.mapSyncDprAttachmentToGridData(
                syncDprProgressAttachments
              ),
              dprDetails.dprProgress.comment
            ).dprprogress;
        }

        if (dprDetails.dprProgressWithoutTarget) {
          const syncDprProgressWithoutTargetDetails =
            this.syncService.getDefaultSyncModel<DprProgressWithoutTargetDetail>();
          const syncDprProgressWithoutTargetAttachments =
            this.syncService.getDefaultSyncModel<DprAttachment>();

          syncDprProgressWithoutTargetDetails.addedList = (
            dprDetails.dprProgressWithoutTarget
              .dprProgressWithoutTargetDetails || []
          ).filter((x) => x.clearedToday != null);
          syncDprProgressWithoutTargetAttachments.addedList =
            dprDetails.dprProgressWithoutTarget
              .dprProgressWithoutTargetAttachments || [];

          changes.dprProgressWithoutTarget =
            this.dprDetailsSyncModelBuilderService.forDprProgressWithoutTarget(
              dprDetails.dprSubprojectId,
              dprDetails.id,
              dprDetails.dprProgressWithoutTarget.id,
              syncDprProgressWithoutTargetDetails,
              this.dprAttachmentService.mapSyncDprAttachmentToGridData(
                syncDprProgressWithoutTargetAttachments
              ),
              dprDetails.dprProgressWithoutTarget.comment
            ).dprProgressWithoutTarget;
        }

        if (dprDetails.dprTableOfColumns) {
          let syncDprTableOfColumns = [];

          dprDetails.dprTableOfColumns.forEach((dprTableOfColumn) => {
            const syncDprTableOfColumnsDetails =
              this.syncService.getDefaultSyncModel<DprTableOfColumnDetail>();
            const syncDprTableOfColumnsAttachments =
              this.syncService.getDefaultSyncModel<DprAttachment>();

            syncDprTableOfColumnsDetails.addedList =
              withoutDerivedTableOfColumnItems(
                dprTableOfColumn.dprTableOfColumnsDetails,
                projectSetup?.tableOfColumns?.find(
                  (x) => +x.id === +dprTableOfColumn.tableOfColumnId
                )
              );
            syncDprTableOfColumnsAttachments.addedList =
              dprTableOfColumn.dprTableOfColumnsAttachment || [];

            const item =
              this.dprDetailsSyncModelBuilderService.forDprTableOfColumns(
                dprDetails.dprSubprojectId,
                dprDetails.id,
                dprTableOfColumn.id,
                dprTableOfColumn.tableOfColumnId,
                syncDprTableOfColumnsDetails,
                this.dprAttachmentService.mapSyncDprAttachmentToGridData(
                  syncDprTableOfColumnsAttachments
                ),
                dprTableOfColumn.comment
              ).dprTableOfColumn;

            syncDprTableOfColumns = [...syncDprTableOfColumns, item];
          });

          changes.dprTableOfColumns = syncDprTableOfColumns;
        }

        if (dprDetails.dprHidden) {
          const syncDprHidden =
            this.syncService.getDefaultSyncModel<DprHidden>();
          dprDetails.dprHidden.forEach((hidden) => {
            const item = {
              id: hidden.id,
              dprDetailId: hidden.dprDetailId,
              hiddenSubtabId: hidden.hiddenSubtabId,
              comment: hidden.comment,
              syncDprHiddenAttachments:
                this.syncService.getDefaultSyncModel<DprAttachment>(),
            };

            item.syncDprHiddenAttachments.addedList =
              hidden.dprHiddenAttachments || [];

            syncDprHidden.addedList = [...syncDprHidden.addedList, item];
          });

          changes.dprHidden =
            this.dprDetailsSyncModelBuilderService.forDprHidden(
              dprDetails.dprSubprojectId,
              dprDetails.id,
              syncDprHidden
            ).syncDprHidden;
        }

        return of(changes);
      })
    );
  }

  //#region Private methods

  private setDprAttachmentIds(item: DprAttachment): void {
    // the dprDetailId present on the backend DTO is set on the API when creating the attachment record,
    // so there's no need to set it here
    item.id = uuid();
    item.attachmentId = null;
    item.attachment.id = null;
  }

  //#endregion
}
